import apiClient from './client';

interface CheckoutSessionResponse {
  checkout_url?: string;
  session_id?: string;
  success?: boolean;
  redirect_url?: string;
  message?: string;
  requires_checkout?: boolean;
  reason?: 'billing_interval_change' | 'plan_downgrade';
}

interface PortalSessionResponse {
  portal_url: string;
}

interface StripeConfigResponse {
  publishable_key: string;
}

export const stripeApi = {
  // Get Stripe publishable key
  async getConfig(): Promise<StripeConfigResponse> {
    const response = await apiClient.get('/stripe/config');
    return response.data;
  },

  // Create checkout session
  async createCheckoutSession(
    planId: number,
    billingCycle: 'monthly' | 'quarterly' | 'annual',
    sessionToken?: string,
    consentData?: { terms_accepted?: boolean; privacy_accepted?: boolean; email_opt_in?: boolean }
  ): Promise<CheckoutSessionResponse> {
    const response = await apiClient.post('/stripe/create-checkout-session', {
      plan_id: planId,
      billing_cycle: billingCycle,
      ...(sessionToken && { session_token: sessionToken }),
      ...(consentData && { consent_data: consentData })
    });
    return response.data;
  },

  // Create checkout session for signup (no auth required)
  async createCheckoutSessionSignup(
    planId: number,
    billingCycle: 'monthly' | 'quarterly' | 'annual',
    sessionToken?: string,
    consentData?: { terms_accepted?: boolean; privacy_accepted?: boolean; email_opt_in?: boolean }
  ): Promise<CheckoutSessionResponse> {
    const response = await apiClient.post('/stripe/create-checkout-session-signup', {
      plan_id: planId,
      billing_cycle: billingCycle,
      ...(sessionToken && { session_token: sessionToken }),
      ...(consentData && { consent_data: consentData })
    });
    return response.data;
  },

  // Create customer portal session
  // flow_type options: 'subscription_cancel', 'subscription_update', 'payment_method_update', or undefined for default portal
  async createPortalSession(flow_type?: string): Promise<PortalSessionResponse> {
    const response = await apiClient.post('/stripe/customer-portal', {
      flow_type: flow_type || null
    });
    return response.data;
  },

  // Get checkout session details
  async getCheckoutSession(sessionId: string) {
    const response = await apiClient.get(`/stripe/checkout-session/${sessionId}`);
    return response.data;
  },

  // Create upgrade checkout session for existing subscriptions
  async createUpgradeSession(planId: number, billingCycle: 'monthly' | 'quarterly' | 'annual'): Promise<CheckoutSessionResponse> {
    const response = await apiClient.post('/subscription/create-upgrade-session', {
      plan_id: planId,
      billing_cycle: billingCycle
    });
    return response.data;
  }
};